import { lazy, Suspense } from 'react';
import MarketingV2App from './surfaces/marketing/v2/MarketingV2App';
import KickstartV2App from './surfaces/kickstart/v2/KickstartV2App';
import OSV2App from './surfaces/os/v2/OSV2App';
import DeckApp from './surfaces/deck/DeckApp';
import FoundryPreviewApp from './surfaces/foundry-preview/FoundryPreviewApp';
import FlywheelApp from './surfaces/flywheel/FlywheelApp';
import LabsSurface from './surfaces/labs/LabsSurface';
import MemberCatalogSurface from './surfaces/library/member/MemberCatalogSurface';

// Owner-review build. The hosted release is separate from this development surface.
const GrowthSurface = import.meta.env.DEV ? lazy(() => import('./surfaces/growth/GrowthSurface')) : null;
const RevenueGoalPlanner = import.meta.env.DEV
  ? lazy(() => import('./surfaces/revenue-goal-planner/RevenueGoalPlanner'))
  : null;
// The product family (Knack, Groundwork, Offer Sheet, Stage Book, Pacer, Take One,
// Ad Angles, Content OS, Amra). Local development only: no release, price or access is implied.
const EcosystemSurface = import.meta.env.DEV
  ? lazy(() => import('./surfaces/ecosystem/EcosystemSurface'))
  : null;
const FAMILY_SURFACES = ['knack', 'groundwork', 'offer-sheet', 'coursework', 'pacer', 'take-one', 'angles', 'content-os', 'amra'] as const;
type FamilySurfaceName = typeof FAMILY_SURFACES[number];
const isFamilySurface = (value: string | null): value is FamilySurfaceName =>
  (FAMILY_SURFACES as readonly string[]).includes(value ?? '');

// Owner review only. These product-development surfaces must not enter the
// production bundle until the offer, course delivery and purchase path are
// approved and verified.
const ProductPossibilityFinder = import.meta.env.DEV
  ? lazy(() => import('./surfaces/product-finder/ProductPossibilityFinder'))
  : null;
const LearningJourney = import.meta.env.DEV ? lazy(() => import('./surfaces/learning-journey/LearningPortalApp')) : null;
const CoursePreview = import.meta.env.DEV
  ? lazy(() => import('./surfaces/course-preview/CoursePreview'))
  : null;
const CourseFunnelSurface = import.meta.env.DEV
  ? lazy(() => import('./surfaces/course-funnel/CourseFunnelSurface'))
  : null;
// Lights On fixture bench (`?surface=lights-on`). DEV-only: the static check
// dead-code-eliminates the bench and its example fixtures from production.
const LightsOnBench = import.meta.env.DEV
  ? lazy(() => import('./surfaces/marketing/v2/lights-on/LightsOnBench'))
  : null;

// Interaction kit gallery (`?surface=moments`). DEV-only: the static check
// dead-code-eliminates the gallery and its example business from production.
const MomentsGallery = import.meta.env.DEV
  ? lazy(() => import('./surfaces/moments/MomentsGallery'))
  : null;

// Concept prototypes (`?surface=concepts`). DEV-only, gated exactly like the
// moments gallery so production builds drop the page and its sample data.
const ConceptsGallery = import.meta.env.DEV
  ? lazy(() => import('./surfaces/concepts/ConceptsGallery'))
  : null;

// Internal R&D cockpit (A2: internal-only). DEV-only by design: the static
// `import.meta.env.DEV` check dead-code-eliminates the import in production
// builds, so the internal catalog (eval evidence, experiment ledger, writeback
// diffs, source paths) never ships in the public bundle. Tom's internal
// instance runs locally (A1); a deployed version belongs behind OS auth.
const LibrarySurface = import.meta.env.DEV
  ? lazy(() => import('./surfaces/library/LibrarySurface'))
  : null;

// Catalog back office (Phase 8). DEV-only by the same mechanism as the library
// cockpit above: `import.meta.env.DEV` is a compile-time constant, so the
// dynamic import is dead-code-eliminated and no customer bundle contains the
// admin editor, the publish gate UI or the performance board.
//
// Wired 2026-08-25 at the owner-attended cutover (pre-launch, owner is
// Customer Number One and manages the live catalog): the production build
// includes the admin chunk when `VITE_ADMIN_SURFACE=enabled` is set in the
// deploy environment. The chunk is lazy — customers never fetch it unless
// they navigate to `?surface=admin`, where non-admins get the sign-in /
// not-available card. The server's `ADMIN_EMAILS` + Convex `requireAdmin`
// checks are the actual security boundary either way. Before public launch,
// revisit splitting a dedicated admin build (see the P8 build log).
// (Vercel Hobby refuses non-sensitive VITE_* production env vars from the
// CLI, so the pre-launch enable is a code constant rather than a deploy
// define. Same effect: the owner reaches ?surface=admin on the live app.)
const ADMIN_SURFACE_ENABLED = true;

const AdminSurface = ADMIN_SURFACE_ENABLED
  ? lazy(() => import('./surfaces/admin/AdminSurface'))
  : null;

// Plot bench (score → plot). DEV-only by the same mechanism as the library
// cockpit: the static `import.meta.env.DEV` check dead-code-eliminates the
// import in production builds, so the placeholder sprite generator, the debug
// overlays and the operator-language readouts never reach a customer bundle.
// The customer-facing plot ships inside /score, not from here.
const PlotSurface = import.meta.env.DEV
  ? lazy(() => import('./surfaces/plot/PlotApp'))
  : null;

// Render rig (mesh -> sprite). DEV-only for the same reason: it pulls three.js,
// the GLTF loader and every source mesh, none of which belong in a customer
// bundle. Stage 3 of docs/design/paperworld/PIPELINE.md.
const RigSurface = import.meta.env.DEV
  ? lazy(() => import('./surfaces/plot/rig/RigApp'))
  : null;

/**
 * Detect which surface to render based on hostname/port.
 *
 * Production:
 *   - theaibrandlab.com / www.theaibrandlab.com  -> 'marketing'
 *   - kickstart.theaibrandlab.com                -> 'kickstart'
 *   - app.theaibrandlab.com                      -> 'os'
 *
 * The legacy non-v2 surfaces have been retired; "marketing" / "kickstart" /
 * "os" now render the v2 (Foundry Race Bib) surfaces. Three surfaces, one
 * design language, end-to-end.
 *
 * Local development:
 *   - localhost:5174 -> 'kickstart'
 *   - localhost:5175 -> 'os'
 *   - localhost:5173 -> 'marketing' (default)
 *
 * URL-param override (`?surface=kickstart|os|marketing|deck|foundry`) is
 * preserved for local dev across surfaces from one port.
 */
type Surface = 'marketing' | 'kickstart' | 'os' | 'deck' | 'foundry' | 'flywheel' | 'labs' | 'library' | 'library-member' | 'plot' | 'rig' | 'admin' | 'product-finder' | 'course-preview' | 'course-funnel' | 'learning-journey';

function detectSurface(): Surface {
  const params = new URLSearchParams(window.location.search);
  const override = params.get('surface');
  if (
    override === 'marketing' ||
    override === 'kickstart' ||
    override === 'os' ||
    override === 'deck' ||
    override === 'foundry' ||
    override === 'flywheel' ||
    override === 'labs' ||
    override === 'library' ||
    override === 'library-member' ||
    override === 'plot' ||
    override === 'rig' ||
    override === 'admin' ||
    override === 'product-finder' ||
    override === 'course-preview' ||
    override === 'course-funnel' || override === 'learning-journey'
  ) {
    return override;
  }

  const hostname = window.location.hostname;
  const port = window.location.port;

  // Internal read-only Skill Library cockpit (catalog projection).
  if (hostname.startsWith('library.') || (hostname === 'localhost' && port === '5177')) return 'library';
  // Catalog back office. DEV-only; a dedicated admin build would own this host.
  if (hostname.startsWith('admin.') || (hostname === 'localhost' && port === '5178')) return 'admin';
  // Public, no-auth interactive tools surface.
  if (hostname.startsWith('tools.') || (hostname === 'localhost' && port === '5176')) return 'labs';
  if (hostname.startsWith('kickstart.') || (hostname === 'localhost' && port === '5174')) return 'kickstart';
  if (hostname.startsWith('app.') || (hostname === 'localhost' && port === '5175')) return 'os';
  return 'marketing';
}

const surface = detectSurface();

function App() {
  // Product routes replace ecosystem chrome. This local pilot is excluded
  // from production until catalog access and persisted delivery are ready.
  if (RevenueGoalPlanner && new URLSearchParams(window.location.search).get('surface') === 'revenue-goal-planner') {
    return <Suspense fallback={null}><RevenueGoalPlanner /></Suspense>;
  }
  const familySurface = new URLSearchParams(window.location.search).get('surface');
  if (EcosystemSurface && isFamilySurface(familySurface)) {
    return <Suspense fallback={null}><EcosystemSurface surface={familySurface} /></Suspense>;
  }
  if (surface === 'learning-journey' && LearningJourney) return <Suspense fallback={null}><LearningJourney localPreview={new URLSearchParams(window.location.search).get('mode') !== 'live'} /></Suspense>;
  if (surface === 'product-finder' && ProductPossibilityFinder) {
    return <Suspense fallback={null}><ProductPossibilityFinder /></Suspense>;
  }
  if (surface === 'course-preview' && CoursePreview) {
    return <Suspense fallback={null}><CoursePreview /></Suspense>;
  }
  if (surface === 'course-funnel' && CourseFunnelSurface) {
    return <Suspense fallback={null}><CourseFunnelSurface /></Suspense>;
  }
  if (LightsOnBench && new URLSearchParams(window.location.search).get('surface') === 'lights-on') {
    return <Suspense fallback={null}><LightsOnBench /></Suspense>;
  }
  if (MomentsGallery && new URLSearchParams(window.location.search).get('surface') === 'moments') {
    return <Suspense fallback={null}><MomentsGallery /></Suspense>;
  }
  if (ConceptsGallery && new URLSearchParams(window.location.search).get('surface') === 'concepts') {
    return <Suspense fallback={null}><ConceptsGallery /></Suspense>;
  }
  if (GrowthSurface && new URLSearchParams(window.location.search).get('surface') === 'growth') {
    return <Suspense fallback={null}><GrowthSurface /></Suspense>;
  }
  // Route to the correct surface — each surface renders its own standalone component.
  // This switch is above all hooks intentionally: each surface manages its own
  // Clerk context and hooks, and detectSurface() returns a constant for the
  // lifetime of the page.
  if (surface === 'foundry') return <FoundryPreviewApp />;
  if (surface === 'flywheel') return <FlywheelApp />;
  if (surface === 'library') {
    // Prod builds exclude the internal cockpit — fall back to marketing.
    if (!LibrarySurface) return <MarketingV2App />;
    return (
      <Suspense fallback={null}>
        <LibrarySurface />
      </Suspense>
    );
  }
  if (surface === 'admin') {
    // Prod builds exclude the back office — fall back to marketing.
    if (!AdminSurface) return <MarketingV2App />;
    return (
      <Suspense fallback={null}>
        <AdminSurface />
      </Suspense>
    );
  }
  if (surface === 'plot') {
    // Prod builds exclude the bench — fall back to marketing.
    if (!PlotSurface) return <MarketingV2App />;
    return (
      <Suspense fallback={null}>
        <PlotSurface />
      </Suspense>
    );
  }
  if (surface === 'rig') {
    // Prod builds exclude the rig — fall back to marketing.
    if (!RigSurface) return <MarketingV2App />;
    return (
      <Suspense fallback={null}>
        <RigSurface />
      </Suspense>
    );
  }
  if (surface === 'library-member') return <MemberCatalogSurface />;
  if (surface === 'labs')      return <LabsSurface />;
  if (surface === 'kickstart') return <KickstartV2App />;
  if (surface === 'os')        return <OSV2App />;
  if (surface === 'deck')      return <DeckApp />;
  return <MarketingV2App />;
}

export default App;
